/**
 * Grade Conversion Utilities
 *
 * Bidirectional conversion between V-scale and Font scale grading systems.
 *
 * V-scale (Vermin Scale) is primarily used in the USA.
 * Font scale (Fontainebleau) is primarily used in Europe.
 *
 * Conversion table (approximate mappings):
 * V0 ≈ 4 | V1 ≈ 5 | V2 ≈ 5+ | V3 ≈ 6a | V4 ≈ 6b | V5 ≈ 6c
 * V6 ≈ 7a | V7 ≈ 7a+ | V8 ≈ 7b+ | V9 ≈ 7c | V10 ≈ 7c+
 * V11 ≈ 8a | V12 ≈ 8a+ | V13 ≈ 8b | V14 ≈ 8b+ | V15 ≈ 8c
 * V16 ≈ 8c+ | V17 ≈ 9a
 */

import type { VGrade, GradeModifier } from '@flash/shared';

/**
 * All valid Font grades in ascending order of difficulty.
 * Mapped 1:1 with V_GRADES for bidirectional conversion.
 */
export const FONT_GRADES = [
  '3',
  '4',
  '5',
  '5+',
  '6a',
  '6b',
  '6c',
  '7a',
  '7a+',
  '7b+',
  '7c',
  '7c+',
  '8a',
  '8a+',
  '8b',
  '8b+',
  '8c',
  '8c+',
  '9a',
] as const;

/**
 * Font grade type representing valid Fontainebleau grades.
 */
export type FontGrade = (typeof FONT_GRADES)[number];

/**
 * V-grades array matching the FONT_GRADES index positions.
 */
const V_GRADES: VGrade[] = [
  'VB',
  'V0',
  'V1',
  'V2',
  'V3',
  'V4',
  'V5',
  'V6',
  'V7',
  'V8',
  'V9',
  'V10',
  'V11',
  'V12',
  'V13',
  'V14',
  'V15',
  'V16',
  'V17',
];

/**
 * Type guard to check if a value is a valid FontGrade.
 */
export const isFontGrade = (value: unknown): value is FontGrade => {
  return typeof value === 'string' && FONT_GRADES.includes(value as FontGrade);
};

/**
 * Convert a V-grade to its Font equivalent.
 *
 * @param vGrade - The V-grade to convert (V0-V17)
 * @returns The corresponding Font grade
 *
 * @example
 * ```ts
 * vToFont('V4') // Returns '6b'
 * vToFont('V10') // Returns '7c+'
 * ```
 */
export const vToFont = (vGrade: VGrade): FontGrade => {
  const index = V_GRADES.indexOf(vGrade);
  // Index is guaranteed valid since VGrade is constrained to valid values
  return FONT_GRADES[index] as FontGrade;
};

/**
 * Convert a Font grade to its V-scale equivalent.
 *
 * @param fontGrade - The Font grade to convert
 * @returns The corresponding V-grade
 *
 * @example
 * ```ts
 * fontToV('6b') // Returns 'V4'
 * fontToV('7c+') // Returns 'V10'
 * ```
 */
export const fontToV = (fontGrade: FontGrade): VGrade => {
  const index = FONT_GRADES.indexOf(fontGrade);
  // Index is guaranteed valid since FontGrade is constrained to valid values
  return V_GRADES[index] as VGrade;
};

/**
 * Format a V-grade with an optional modifier.
 *
 * @param grade - The base V-grade (V0-V17)
 * @param modifier - Optional modifier ('+', '-', or null)
 * @returns Formatted grade string (e.g., "V4+", "V5-", "V3")
 *
 * @example
 * ```ts
 * formatGradeWithModifier('V4', '+') // Returns 'V4+'
 * formatGradeWithModifier('V5', '-') // Returns 'V5-'
 * formatGradeWithModifier('V3', null) // Returns 'V3'
 * ```
 */
export const formatGradeWithModifier = (grade: VGrade, modifier: GradeModifier | null): string => {
  if (!modifier) {
    return grade;
  }
  return `${grade}${modifier}`;
};

/**
 * Parse a grade string that may contain a modifier.
 *
 * @param gradeString - Grade string (e.g., "V4+", "V5-", "V3")
 * @returns Object with parsed grade and modifier, or null if invalid
 *
 * @example
 * ```ts
 * parseGradeWithModifier('V4+') // Returns { grade: 'V4', modifier: '+' }
 * parseGradeWithModifier('V5-') // Returns { grade: 'V5', modifier: '-' }
 * parseGradeWithModifier('V3')  // Returns { grade: 'V3', modifier: null }
 * parseGradeWithModifier('invalid') // Returns null
 * ```
 */
export const parseGradeWithModifier = (
  gradeString: string
): { grade: VGrade; modifier: GradeModifier } | null => {
  // Match V-scale pattern: Vb or V followed by digits, optionally followed by + or -
  const match = gradeString.match(/^(VB|V\d+)([+-])?$/);
  if (!match) {
    return null;
  }

  const [, gradeStr, modifierStr] = match;

  // Validate the grade is a valid VGrade
  if (!V_GRADES.includes(gradeStr as VGrade)) {
    return null;
  }

  const modifier: GradeModifier = modifierStr === '+' ? '+' : modifierStr === '-' ? '-' : null;

  return {
    grade: gradeStr as VGrade,
    modifier,
  };
};

/**
 * Convert a V-grade with modifier to the specified display format.
 *
 * For V-scale output, preserves the modifier (e.g., "V4+").
 * For Font scale output, adds the modifier to the converted grade (e.g., "6b+").
 *
 * @param grade - The base V-grade
 * @param modifier - Optional modifier
 * @param targetSystem - Target grading system ('v-scale' or 'font')
 * @returns Formatted grade string in the target system
 *
 * @example
 * ```ts
 * convertWithModifier('V4', '+', 'v-scale') // Returns 'V4+'
 * convertWithModifier('V4', '+', 'font')    // Returns '6b+'
 * convertWithModifier('V3', null, 'font')   // Returns '6a'
 * ```
 */
export const convertWithModifier = (
  grade: VGrade,
  modifier: GradeModifier | null,
  targetSystem: 'v-scale' | 'font'
): string => {
  if (targetSystem === 'v-scale') {
    return formatGradeWithModifier(grade, modifier);
  }

  // Convert to Font scale
  const fontGrade = vToFont(grade);
  if (!modifier) {
    return fontGrade;
  }
  return `${fontGrade}${modifier}`;
};
